import { getLocalizedTextDefault, type LibraryQuestion } from "@/api/survey-library.service";
import { supportsNoExperienceLabel } from "../questionTypes";
import type { SurveyDefinition, SurveyDefinitionElement } from "./surveyDetails.types";

const FOOTER_ELEMENT_NAME = "footer";
const PREVIEW_RANDOM_PAGE_NAME = "additional_questions";

function libraryTypeToSurveyJs(type: string): string {
  const mapping: Record<string, string> = {
    text: "text",
    choice: "radiogroup",
    dropdown: "dropdown",
    "range-10": "range",
    "range-100": "range",
    boolean: "boolean",
    testimonial: "comment",
    "multi-testimonial": "multi-testimonial",
  };
  return mapping[type] ?? "text";
}

/** Mirrors survey-api `toSurveyJsElement` for staff preview only. */
export function libraryQuestionToSurveyJsElement(q: LibraryQuestion): SurveyDefinitionElement {
  const element: SurveyDefinitionElement = {
    type: libraryTypeToSurveyJs(q.type),
    name: q.id,
    title: q.text,
    isRequired: q.required,
  };

  if (q.type === "range-10" || q.type === "range-100") {
    element.rateMin = 0;
    element.rateMax = q.type === "range-10" ? 10 : 100;
    element.libraryQuestionType = q.type;
    if (Array.isArray(q.rangeLabels) && q.rangeLabels.length > 0) {
      element.rangeLabels = q.rangeLabels;
    }
  }

  if (supportsNoExperienceLabel(q.type) && q.noExperienceLabel) {
    element.noExperienceLabel = q.noExperienceLabel;
  }

  if (q.type === "multi-testimonial") {
    element.libraryQuestionType = "multi-testimonial";
    // Propagate the per-locale subjectLabel onto the preview element so the
    // renderer shows the correct "+ {label}" add button in the staff preview.
    if (q.subjectLabel) {
      element.subjectLabel = q.subjectLabel;
    }
  }

  if (q.type === "testimonial") {
    element.libraryQuestionType = "testimonial";
  }

  if (q.description) {
    element.description = q.description;
  }

  if (q.defaultValue !== null) {
    element.defaultValue = q.defaultValue;
  }

  if (q.options && q.options.length > 0) {
    element.choices = q.options;
  }

  return element;
}

function moveFooterToLastPage(definition: SurveyDefinition): SurveyDefinition {
  const pages = definition.pages ?? [];
  if (pages.length < 2) {
    return definition;
  }

  let footerElement: SurveyDefinitionElement | null = null;
  const strippedPages = pages.map(page => {
    const remaining: SurveyDefinitionElement[] = [];
    for (const element of page.elements ?? []) {
      if (element.name === FOOTER_ELEMENT_NAME && footerElement === null) {
        footerElement = element;
        continue;
      }
      remaining.push(element);
    }
    return { ...page, elements: remaining };
  });

  if (!footerElement) {
    return definition;
  }

  const lastIndex = strippedPages.length - 1;
  const lastPage = strippedPages[lastIndex];
  strippedPages[lastIndex] = {
    ...lastPage,
    elements: [...(lastPage.elements ?? []), footerElement],
  };

  return { ...definition, pages: strippedPages };
}

/**
 * Appends a single random-library question after survey content, before the footer
 * (same page layout as production `additional_questions`).
 */
export function appendRandomQuestionForPreview(
  definition: SurveyDefinition,
  element: SurveyDefinitionElement,
): SurveyDefinition {
  const pages = [...(definition.pages ?? [])];
  const existingIndex = pages.findIndex(page => page.name === PREVIEW_RANDOM_PAGE_NAME);

  if (existingIndex >= 0) {
    pages[existingIndex] = { name: PREVIEW_RANDOM_PAGE_NAME, elements: [element] };
  } else {
    pages.push({ name: PREVIEW_RANDOM_PAGE_NAME, elements: [element] });
  }

  return moveFooterToLastPage({ ...definition, pages });
}

export function getRandomQuestionOptionLabel(question: LibraryQuestion): string {
  const internal = question.internalLabel?.trim();
  if (internal) {
    return internal;
  }
  const text = getLocalizedTextDefault(question.text).trim();
  return text.length > 0 ? text : question.id;
}
